import { useEffect, useState } from 'react';
import { HardDrive } from 'lucide-react';
import type { StorageSettings, SupportSettings } from '@dru/types';
import {
  Alert, Button, Card, CardBody, CardHeader, Checkbox, StatusBadge, Tabs, TextField,
} from '@dru/ui';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '@dru/api-client';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { Audit, NotForYourRole } from './Audit';
import { IntegrationsPanel, SystemStatusPanel } from '../components/SystemStatus';
import { NotificationSettings } from '../components/NotificationSettings';
import { SmtpCard, TemplatesCard } from '../components/EmailSettingsParts';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { PartnerNetworkTypesCard } from '../components/PartnerNetworkTypesCard';

/**
 * Platform settings: outgoing email, the support contact, message wording.
 *
 * A value saved here wins over the environment; a blank falls back to it,
 * and the screen says which is in effect. The SMTP password is write-only:
 * the API answers "set" or "not set", and a blank password box on save
 * leaves whatever is stored alone.
 */
type SettingsTab = 'status' | 'integrations' | 'storage' | 'notifications' | 'audit' | 'general';

/**
 * Settings (owner, 29 Oct 2026): what keeps the platform running, split from
 * Email. System status (is the API up, when was it down), Integrations (is
 * every outside service working), the audit log (moved here from the
 * sidebar), and General (support contact, lab network types).
 */
export function Settings() {
  const { permissions } = useAuth();
  const [params, setParams] = useSearchParams();
  const manage = permissions.has('system.settings-manage');
  const audit = permissions.has('audit.read');
  if (!manage && !audit) {
    return <NotForYourRole title="Settings" what="Platform settings are managed by platform administrators." />;
  }
  const tabs: Array<{ key: SettingsTab; label: string; visible: boolean }> = [
    { key: 'status', label: 'System status', visible: manage },
    { key: 'integrations', label: 'Integrations', visible: manage },
    { key: 'storage', label: 'File storage', visible: manage },
    { key: 'notifications', label: 'Notifications', visible: manage },
    { key: 'audit', label: 'Audit log', visible: audit },
    { key: 'general', label: 'General', visible: manage },
  ];
  const shown = tabs.filter((t) => t.visible);
  const asked = params.get('tab') as SettingsTab | null;
  const tab: SettingsTab = shown.some((t) => t.key === asked) ? asked! : shown[0]!.key;
  const setTab = (t: string) => { const next = new URLSearchParams(params); next.set('tab', t); setParams(next, { replace: true }); };

  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="Settings"
        hint="Is the platform running and is everything it uses working; where uploaded files are kept; which notifications go out and how; who did what (the audit log); and the support contact."
      />
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === 'status' && <SystemStatusPanel />}
      {tab === 'integrations' && <IntegrationsPanel />}
      {tab === 'storage' && <div className="max-w-4xl"><StorageCard /></div>}
      {tab === 'notifications' && <NotificationSettings />}
      {tab === 'audit' && <Audit embedded />}
      {tab === 'general' && (
        <div className="max-w-4xl space-y-5">
          <SupportCard />
          <PartnerNetworkTypesCard />
        </div>
      )}
    </div>
  );
}

type EmailTab = 'smtp' | 'templates';

/** Email (owner, 29 Oct 2026): the mail server, and what the messages say. */
export function EmailSettings() {
  const { permissions } = useAuth();
  const [params, setParams] = useSearchParams();
  if (!permissions.has('system.settings-manage')) {
    return <NotForYourRole title="Email" what="Platform email is managed by platform administrators." />;
  }
  const tab: EmailTab = params.get('tab') === 'templates' ? 'templates' : 'smtp';
  const setTab = (t: string) => { const next = new URLSearchParams(params); next.set('tab', t); setParams(next, { replace: true }); };
  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="Email"
        hint="The server the platform sends mail through, and the wording of each message it sends."
      />
      <Tabs tabs={[{ key: 'smtp', label: 'SMTP server' }, { key: 'templates', label: 'Message templates' }]} active={tab} onChange={setTab} />
      <div className="max-w-4xl">
        {tab === 'smtp' ? <SmtpCard /> : <TemplatesCard />}
      </div>
    </div>
  );
}

/**
 * File storage: the Tigris bucket every upload and generated document goes to
 * — photos, signatures, certificates, reports, attachments, payment proofs.
 * Same rules as SMTP: a saved value wins over TIGRIS_* in the environment, a
 * blank falls back to it, and the secret key is write-only.
 */
function StorageCard() {
  const storage = useQuery<StorageSettings>(() => api.get('/admin/settings/storage'), []);
  const [form, setForm] = useState<Record<string, string>>({});
  const [clearSecret, setClearSecret] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [testing, setTesting] = useState(false);

  const d = storage.data;
  useEffect(() => {
    if (!d || loaded) return;
    const f = d.fields;
    setForm({
      accessKeyId: f.accessKeyId.value ?? '', secretAccessKey: '', bucket: f.bucket.value ?? '',
      endpoint: f.endpoint.value ?? '', region: f.region.value ?? '',
    });
    setLoaded(true);
  }, [d, loaded]);

  const set = (k: string) => (v: string) => {
    setForm((x) => ({ ...x, [k]: v }));
    setFieldErrors((e) => { const { [k]: _, ...rest } = e; return rest; });
  };

  async function save() {
    setBusy(true); setError(null); setSaved(null); setFieldErrors({}); setTestResult(null);
    try {
      await api.put('/admin/settings/storage', {
        accessKeyId: form.accessKeyId.trim() || null,
        secretAccessKey: clearSecret ? null : form.secretAccessKey.trim(),
        bucket: form.bucket.trim() || null,
        endpoint: form.endpoint.trim() || null,
        region: form.region.trim() || null,
      });
      setSaved('Saved. New uploads go to this bucket within a few seconds; files already on the server’s disk stay readable.');
      setForm((x) => ({ ...x, secretAccessKey: '' }));
      setClearSecret(false);
      setLoaded(false);
      storage.reload();
    } catch (e) {
      if (e instanceof ApiError) setFieldErrors(e.fieldErrors);
      setError(errorMessage(e));
    } finally { setBusy(false); }
  }

  async function test() {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post<{ ok: boolean; bucket?: string; ms?: number; error?: string }>('/admin/settings/storage/test', {});
      setTestResult(r.ok
        ? { ok: true, text: `Bucket “${r.bucket}” works: a test file was written, read back and deleted (${r.ms} ms).` }
        : { ok: false, text: r.error ?? 'The storage test failed.' });
    } catch (e) { setTestResult({ ok: false, text: errorMessage(e) }); } finally { setTesting(false); }
  }

  const envHint = (k: keyof StorageSettings['fields']) => d?.fields[k].fromEnv ? 'From the environment — a value here overrides it.' : undefined;

  return (
    <Card>
      <CardHeader
        title="File storage (Tigris)"
        hint="Employee photos, signatures, certificates, reports, attachments and payment proofs are stored here."
        actions={d && (
          <StatusBadge tone={d.configured ? 'covered' : 'overdue'}>
            {d.configured ? `configured · ${d.source?.toLowerCase()}` : 'server disk'}
          </StatusBadge>
        )}
      />
      <CardBody className="space-y-4">
        {storage.error && <Alert tone="danger">{storage.error}</Alert>}
        {d && !d.configured && (
          <Alert tone="warning" title="Files are on this server’s disk">
            Until an access key, a secret key and a bucket are saved (or set as TIGRIS_STORAGE_* in the
            environment), uploads are written to the server’s disk, which is not backed up with the database.
          </Alert>
        )}
        {d?.configured && (
          <p className="text-small text-neutral-700">
            Uploads go to <span className="font-mono">{d.bucket}</span> at <span className="font-mono">{d.endpoint}</span>.
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField label="Access key ID" value={form.accessKeyId ?? ''} onChange={set('accessKeyId')} mono error={fieldErrors.accessKeyId} status={envHint('accessKeyId')} placeholder="tid_…" />
          <div>
            <TextField label="Secret access key" value={form.secretAccessKey ?? ''} onChange={set('secretAccessKey')} mono type="password" error={fieldErrors.secretAccessKey}
              placeholder={d?.secretSet ? '•••••••• (set — leave blank to keep)' : 'Not set'}
              status={d?.secretFromEnv ? 'From the environment. Typing one here overrides it.' : 'Stored encrypted. Never shown again.'} />
            {d?.secretSet && !d.secretFromEnv && (
              <Checkbox className="mt-1" label={<span className="text-caption">Clear the stored secret key</span>} checked={clearSecret} onChange={(e) => setClearSecret(e.target.checked)} />
            )}
          </div>
          <TextField label="Bucket" value={form.bucket ?? ''} onChange={set('bucket')} mono error={fieldErrors.bucket} status={envHint('bucket')} placeholder="drultimate" />
          <TextField label="Endpoint" value={form.endpoint ?? ''} onChange={set('endpoint')} mono error={fieldErrors.endpoint} status={envHint('endpoint') ?? 'Blank uses https://t3.storage.dev'} placeholder="https://t3.storage.dev" />
          <TextField label="Region" value={form.region ?? ''} onChange={set('region')} mono error={fieldErrors.region} status={envHint('region') ?? 'Blank uses auto'} placeholder="auto" />
        </div>
        {error && <Alert tone="danger">{error}</Alert>}
        {saved && <Alert tone="success">{saved}</Alert>}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-100 pt-4">
          <Button variant="secondary" onClick={test} loading={testing} disabled={!d?.configured} icon={<HardDrive className="h-4 w-4" />}>
            Test connection
          </Button>
          <Button onClick={save} loading={busy}>Save storage settings</Button>
        </div>
        {testResult && (
          <Alert tone={testResult.ok ? 'success' : 'danger'}>{testResult.text}</Alert>
        )}
        <p className="text-caption text-neutral-500">
          Save before testing — the test uses what is stored, not what is typed.
        </p>
      </CardBody>
    </Card>
  );
}

function SupportCard() {
  const support = useQuery<SupportSettings>(() => api.get('/admin/settings/support'), []);
  const [form, setForm] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  const d = support.data;
  useEffect(() => {
    if (!d || loaded) return;
    const f = d.fields;
    setForm({
      email: f.email.value ?? '', phone: f.phone.value ?? '', hours: f.hours.value ?? '',
      consoleUrl: f.consoleUrl.value ?? '', employeeUrl: f.employeeUrl.value ?? '',
    });
    setLoaded(true);
  }, [d, loaded]);

  async function save() {
    setBusy(true); setError(null); setSaved(false); setFieldErrors({});
    try {
      await api.put('/admin/settings/support', {
        email: form.email.trim() || null, phone: form.phone.trim() || null, hours: form.hours.trim() || null,
        consoleUrl: form.consoleUrl.trim() || null, employeeUrl: form.employeeUrl.trim() || null,
      });
      setSaved(true); setLoaded(false); support.reload();
    } catch (e) {
      if (e instanceof ApiError) setFieldErrors(e.fieldErrors);
      setError(errorMessage(e));
    } finally { setBusy(false); }
  }

  const set = (k: string) => (v: string) => setForm((x) => ({ ...x, [k]: v }));

  return (
    <Card>
      <CardHeader title="Support contact and links" hint="Printed in the welcome email and anywhere a customer is told who to call." />
      <CardBody className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField label="Support email" value={form.email ?? ''} onChange={set('email')} mono type="email" error={fieldErrors.email} />
          <TextField label="Support phone" value={form.phone ?? ''} onChange={set('phone')} mono error={fieldErrors.phone} />
          <TextField label="Support hours" value={form.hours ?? ''} onChange={set('hours')} error={fieldErrors.hours} placeholder="Mon–Sat, 9:00–18:00 IST" />
          <TextField label="Console address" value={form.consoleUrl ?? ''} onChange={set('consoleUrl')} mono error={fieldErrors.consoleUrl}
            hint="The sign-in link in the welcome email." placeholder="https://drultimate.pravinexsolutions.in" />
          <TextField label="Employee app address" value={form.employeeUrl ?? ''} onChange={set('employeeUrl')} mono error={fieldErrors.employeeUrl} className="sm:col-span-2" />
        </div>
        {error && <Alert tone="danger">{error}</Alert>}
        {saved && <Alert tone="success">Saved.</Alert>}
        <div className="flex justify-end"><Button onClick={save} loading={busy}>Save support details</Button></div>
      </CardBody>
    </Card>
  );
}
